import { useEffect, useState } from 'react';
import type { Sponsor } from '../../lib/partners';

// The sidebar sponsor rail with real partners: one logo at a time, cross-fading to the next every
// few seconds. Same frame and "Sponsored" label as the SponsorSlot placeholder, so a partner logo
// still reads as an ad and never as app content.
interface SponsorRailProps {
  sponsors: Sponsor[];
  /** How long each logo stays up, in ms. */
  intervalMs?: number;
  className?: string;
}

const DEFAULT_INTERVAL_MS = 8000;

export function SponsorRail({ sponsors, intervalMs = DEFAULT_INTERVAL_MS, className }: SponsorRailProps) {
  const [index, setIndex] = useState(0);
  // Hover/focus pauses the rotation — a logo that swaps out from under the cursor right as the
  // referee goes to click it is exactly the moment rotation becomes annoying.
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (sponsors.length < 2 || paused) return;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % sponsors.length), intervalMs);
    return () => window.clearInterval(id);
  }, [sponsors.length, intervalMs, paused]);

  if (sponsors.length === 0) return null;
  // Clamp: guards a shrinking sponsors list leaving index past the end until the next tick.
  const current = sponsors[index % sponsors.length];

  return (
    <div
      className={'flex flex-col rounded border border-line bg-surface p-3 ' + (className ?? '')}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-2">Sponsored</span>
        {sponsors.length > 1 && (
          // Position dots — purely decorative, the rotation itself is the only control.
          <span className="flex gap-1" aria-hidden="true">
            {sponsors.map((s, i) => (
              <span
                key={s.logoSrc}
                className={'h-1.5 w-1.5 rounded-full ' + (i === index % sponsors.length ? 'bg-accent' : 'bg-line-2')}
              />
            ))}
          </span>
        )}
      </div>

      {/* Every logo is mounted and stacked, only the active one opaque: the images are loaded up
          front, so a swap is a pure cross-fade instead of a blank frame while the next one fetches. */}
      <div className="relative min-h-[120px] flex-1">
        {sponsors.map((sponsor, i) => {
          const active = i === index % sponsors.length;
          const logo = (
            <img src={sponsor.logoSrc} alt={sponsor.name} className="max-h-[96px] w-full max-w-[200px] object-contain" />
          );
          const layer =
            'absolute inset-0 flex items-center justify-center rounded-sm px-2 transition-opacity duration-700 motion-reduce:transition-none ' +
            (active ? 'opacity-100' : 'pointer-events-none opacity-0');
          // Hidden layers are removed from the a11y tree and tab order, so a screen reader or
          // keyboard user only ever meets the logo that is actually on screen.
          return sponsor.href ? (
            // noopener/noreferrer: a third-party site must not get a handle on the ProRef tab.
            <a
              key={sponsor.logoSrc}
              href={sponsor.href}
              target="_blank"
              rel="noopener noreferrer sponsored"
              aria-hidden={!active}
              tabIndex={active ? 0 : -1}
              className={layer + ' hover:opacity-80'}
            >
              {logo}
            </a>
          ) : (
            <div key={sponsor.logoSrc} aria-hidden={!active} className={layer}>
              {logo}
            </div>
          );
        })}
      </div>

      <p className="mt-2 text-[11px] leading-snug text-muted">{current?.name}</p>
    </div>
  );
}
